import * as React from 'react'

import {
  useFieldBinding,
  type UseFieldBindingProps
} from '@/hooks/use-field-binding'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import type { FieldPathByValue, FieldValues } from 'react-hook-form'
import { FieldWrapper } from '../enhanced/field-wrapper'
import { inputVariants } from './input.variants'

export type InputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPathByValue<TFieldValues, string> = FieldPathByValue<
    TFieldValues,
    string
  >
> = UseFieldBindingProps<TFieldValues, TName> & {
  label?: React.ReactNode
  startIcon?: LucideIcon
  endIcon?: LucideIcon
} & Omit<
    React.ComponentProps<'input'>,
    'name' | 'value' | 'onChange' | 'onBlur' | 'disabled'
  >

function Input<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPathByValue<TFieldValues, string> = FieldPathByValue<
    TFieldValues,
    string
  >
>(props: InputProps<TFieldValues, TName>) {
  const { className, type, startIcon, endIcon, label, ...bindingAndNative } =
    props
  const field = useFieldBinding(bindingAndNative)
  const {
    control: _control,
    name: _name,
    value: _value,
    onChange: _onChange,
    onBlur: _onBlur,
    error: _error,
    disabled: _disabled,
    ...nativeProps
  } = bindingAndNative

  const StartIcon = startIcon
  const EndIcon = endIcon

  return (
    <FieldWrapper
      label={label}
      error={field.error}
      controlId={nativeProps.id}
      className='w-full'
    >
      {({ controlId, errorId }) => (
        <div className='relative w-full'>
          {StartIcon && (
            <div className='absolute top-1/2 left-1.5 -translate-y-1/2 transform'>
              <StartIcon size={18} className='text-muted-foreground' />
            </div>
          )}
          <input
            type={type}
            data-slot='input'
            data-cy={field.name}
            {...nativeProps}
            id={controlId}
            name={field.name}
            value={field.value}
            onChange={(event) => field.onChange(event.target.value)}
            onBlur={field.onBlur}
            disabled={field.disabled}
            aria-invalid={field.error ? true : nativeProps['aria-invalid']}
            aria-describedby={errorId ?? nativeProps['aria-describedby']}
            className={cn(
              inputVariants(),
              'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-xs disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
              startIcon ? 'pl-8' : '',
              endIcon ? 'pr-8' : '',
              className
            )}
          />
          {EndIcon && (
            <div className='absolute top-1/2 right-3 -translate-y-1/2 transform'>
              <EndIcon className='text-muted-foreground' size={18} />
            </div>
          )}
        </div>
      )}
    </FieldWrapper>
  )
}

export { Input }
